import { computeOverallScore, getHpGrade } from '../../lib/assessment/content';
import type { FreeResult } from '../../lib/apiClient';

/**
 * The HP Performance Score panel.
 *
 * Production renders this same panel in two places — the free result page and
 * the paid report's hero — from the same markup. V2 had it only on the report;
 * the free result showed `35/100 · D (label)` on one line, with no bar, no
 * grade badge and no caption. Live comparison against production, 2026-09-07.
 *
 * Lifted out of `ReportHero` unchanged so the two pages cannot drift apart
 * again, which is the failure this shape exists to prevent.
 */
export const GRADE_STYLES: Record<string, { panel: string; text: string; bar: string }> = {
  S: { panel: 'border-green-500/30 bg-green-500/15', text: 'text-green-500', bar: 'bg-green-500' },
  A: { panel: 'border-sky-500/30 bg-sky-500/15', text: 'text-sky-500', bar: 'bg-sky-500' },
  B: { panel: 'border-amber-500/30 bg-amber-500/15', text: 'text-amber-500', bar: 'bg-amber-500' },
  C: {
    panel: 'border-orange-500/30 bg-orange-500/15',
    text: 'text-orange-500',
    bar: 'bg-orange-500',
  },
  D: { panel: 'border-red-500/30 bg-red-500/15', text: 'text-red-500', bar: 'bg-red-500' },
};

export function HpScorePanel({
  result,
  testIdPrefix = 'report',
}: {
  result: FreeResult;
  /** `report` on the paid hero, `result` on the free result page. */
  testIdPrefix?: string;
}) {
  const overall = Math.round(computeOverallScore(result));
  const { grade, label: gradeLabel } = getHpGrade(overall);
  const style = GRADE_STYLES[grade] ?? GRADE_STYLES.C!;

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`${testIdPrefix}-hp-score`}>
      <p className="mb-4 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        HP Performance Score
      </p>
      <div className="flex items-center gap-5">
        <div className="min-w-0 flex-1">
          <p className="mb-3 flex items-baseline gap-2">
            <span
              className="text-5xl font-bold leading-none tabular-nums text-primary"
              data-testid={`${testIdPrefix}-hp-value`}
            >
              {overall}
            </span>
            <span className="text-base font-medium text-muted-foreground">/100</span>
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
            <div
              className={`h-full rounded-full transition-all ${style.bar}`}
              style={{ width: `${overall}%` }}
            />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Composite rating across all four performance dimensions.
          </p>
        </div>

        <div className="shrink-0 text-center">
          <span
            className={`inline-flex h-14 w-14 items-center justify-center rounded-xl border text-2xl font-bold ${style.panel} ${style.text}`}
            data-testid={`${testIdPrefix}-hp-grade`}
          >
            {grade}
          </span>
          <p className={`mt-1.5 text-[11px] font-semibold uppercase tracking-wider ${style.text}`}>
            {gradeLabel}
          </p>
        </div>
      </div>
    </div>
  );
}
